import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, Clock, MapPin, Wallet, Trash2, Plus, Pencil, Building2 } from "lucide-react";
import AppShell, { useAppUser } from "@/components/AppShell";
import { authApi, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-obsidian border border-white/10 focus:border-gold/60 rounded-xl px-4 py-3 text-sm text-[#F5F5F0] placeholder:text-zinc-600 outline-none transition-colors duration-200";

const EMPTY = { title: "", date: "", time: "", address: "", cost: "", description: "", unit_ids: [] };

const formatDate = (d) => {
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};

export default function Eventos() {
  const { user } = useAppUser() || {};
  const location = useLocation();
  const [events, setEvents] = useState(null);
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const isAdmin = user && (user.roles || [user.role]).includes("admin");
  const isStaff = user && (user.roles || [user.role]).some((r) => r !== "aluno");

  useEffect(() => {
    const novo = !!new URLSearchParams(location.search).get("novo");
    if (novo) {
      setEditingId(null);
      setForm(EMPTY);
    }
    setFormOpen(novo);
  }, [location.search]);

  const refresh = async () => {
    const res = await authApi().get("/events");
    setEvents(res.data.events);
  };

  useEffect(() => {
    if (!user) return;
    refresh();
    if ((user.roles || [user.role]).some((r) => r !== "aluno")) {
      authApi()
        .get("/units")
        .then((res) => setUnits(res.data.units || []))
        .catch(() => {});
    }
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleUnit = (id) =>
    setForm((f) => ({
      ...f,
      unit_ids: f.unit_ids.includes(id) ? f.unit_ids.filter((x) => x !== id) : [...f.unit_ids, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await authApi().patch(`/events/${editingId}`, form);
        toast.success("Evento atualizado. As unidades convocadas foram notificadas da alteração.");
        setEditingId(null);
      } else {
        await authApi().post("/events", form);
        toast.success("Evento criado e unidades convocadas notificadas.");
      }
      setForm(EMPTY);
      setFormOpen(false);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title,
      date: ev.date,
      time: ev.time,
      address: ev.address,
      cost: ev.cost,
      description: ev.description || "",
      unit_ids: (ev.units || []).map((u) => u.id),
    });
    setFormOpen(true);
    document.querySelector('[data-testid="event-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const closeForm = () => {
    setEditingId(null);
    setForm(EMPTY);
    setFormOpen(false);
  };

  const remove = async (ev) => {
    if (!window.confirm(`Excluir o evento "${ev.title}"?`)) return;
    try {
      await authApi().delete(`/events/${ev.id}`);
      toast.success("Evento removido.");
      if (editingId === ev.id) closeForm();
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <AppShell active="eventos">
      <section data-testid="events-page">
        <h1 className="font-display text-2xl font-bold" data-testid="events-title">Eventos</h1>
        <p className="font-mono text-xs tracking-[0.3em] text-gold mt-2 mb-6" data-testid="events-user-info">
          AGENDA DO PROJETO {events ? `(${events.length})` : ""}
        </p>

        {isStaff && formOpen && (
          <form onSubmit={submit} className="bg-surface border border-white/10 rounded-2xl p-6 grid sm:grid-cols-2 gap-4" data-testid="event-form">
            <p className="sm:col-span-2 font-mono text-xs tracking-[0.3em] text-gold" data-testid="event-form-label">
              {editingId ? "EDITAR EVENTO" : "CRIAR EVENTO"}
            </p>
            <input required minLength={2} value={form.title} onChange={set("title")} placeholder="Nome do evento (ex: Copa Interna)" className={inputCls} data-testid="event-title-input" />
            <input required value={form.cost} onChange={set("cost")} placeholder="Custo (ex: Gratuito ou R$ 50,00)" className={inputCls} data-testid="event-cost-input" />
            <input required type="date" value={form.date} onChange={set("date")} className={`${inputCls} [color-scheme:dark]`} data-testid="event-date-input" />
            <input required type="time" value={form.time} onChange={set("time")} className={`${inputCls} [color-scheme:dark]`} data-testid="event-time-input" />
            <input required minLength={2} value={form.address} onChange={set("address")} placeholder="Endereço" className={`${inputCls} sm:col-span-2`} data-testid="event-address-input" />
            <textarea rows={2} value={form.description} onChange={set("description")} placeholder="Descrição (opcional)" className={`${inputCls} sm:col-span-2 resize-none`} data-testid="event-description-input" />

            <div className="sm:col-span-2">
              <p className="font-mono text-[11px] tracking-widest text-zinc-500 mb-2">CONVOCAR UNIDADES (usuários vinculados serão notificados)</p>
              <div className="flex flex-wrap gap-2" data-testid="event-units-group">
                {units.length === 0 && <p className="text-xs text-zinc-600">Nenhuma unidade cadastrada.</p>}
                {units.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleUnit(u.id)}
                    data-testid={`event-unit-${u.id}`}
                    className={`font-mono text-[11px] tracking-wider px-3 py-2 rounded-full border transition-colors ${form.unit_ids.includes(u.id) ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-500 hover:border-gold/40"}`}
                  >
                    {u.name.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <button type="submit" disabled={saving} data-testid="event-submit-button" className="sm:col-span-2 inline-flex items-center justify-center gap-3 font-display font-semibold bg-gold text-obsidian px-8 py-3.5 rounded-full hover:bg-gold-amber disabled:opacity-60 transition-all duration-300">
              <Plus size={18} /> {saving ? "Salvando..." : editingId ? "Salvar Alterações" : "Criar Evento"}
            </button>
            <button type="button" onClick={closeForm} data-testid="event-form-close" className="sm:col-span-2 font-mono text-xs tracking-widest border border-white/15 text-zinc-400 px-8 py-3 rounded-full hover:border-crimson/60 hover:text-crimson transition-colors">
              {editingId ? "CANCELAR EDIÇÃO" : "FECHAR"}
            </button>
          </form>
        )}

        <div className="mt-8" data-testid="events-list-section">
          {events === null ? (
            <p className="text-zinc-500 text-sm">Carregando eventos...</p>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center gap-4 border border-dashed border-white/15 rounded-2xl py-16 text-zinc-500" data-testid="events-empty">
              <CalendarDays size={32} />
              <p className="text-sm">Nenhum evento programado no momento.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5" data-testid="events-list">
              {events.map((ev, i) => (
                <motion.article
                  key={ev.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06, duration: 0.4 }}
                  className="bg-[#1C1C23] border border-white/10 hover:border-gold/40 rounded-2xl p-6 transition-colors"
                  data-testid={`event-card-${i}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="font-display font-semibold text-lg leading-snug">{ev.title}</h2>
                    <div className="flex gap-2 shrink-0">
                      {isStaff && (
                        <button onClick={() => startEdit(ev)} data-testid={`event-edit-${i}`} className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center text-zinc-500 hover:text-gold hover:border-gold/50 transition-colors" aria-label={`Editar ${ev.title}`}>
                          <Pencil size={14} />
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => remove(ev)} data-testid={`event-delete-${i}`} className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center text-zinc-500 hover:text-crimson hover:border-crimson/60 transition-colors" aria-label={`Excluir ${ev.title}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 space-y-2.5 text-sm text-zinc-300">
                    <p className="flex items-center gap-2.5" data-testid={`event-date-${i}`}>
                      <CalendarDays size={15} className="text-gold shrink-0" />
                      <span className="font-mono">{formatDate(ev.date)}</span>
                      <Clock size={15} className="text-gold shrink-0 ml-2" />
                      <span className="font-mono">{ev.time}</span>
                    </p>
                    <p className="flex items-center gap-2.5" data-testid={`event-address-${i}`}>
                      <MapPin size={15} className="text-crimson shrink-0" /> {ev.address}
                    </p>
                    <p className="flex items-center gap-2.5" data-testid={`event-cost-${i}`}>
                      <Wallet size={15} className="text-gold shrink-0" />
                      <span className={`font-mono text-xs tracking-wider px-3 py-1 rounded-full border ${/gratuito|grátis|free/i.test(ev.cost) ? "border-green-500/50 text-green-400 bg-green-500/10" : "border-gold/40 text-gold bg-gold/10"}`}>
                        {ev.cost}
                      </span>
                    </p>
                  </div>
                  {(ev.units || []).length > 0 && (
                    <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid={`event-units-${i}`}>
                      <Building2 size={14} className="text-gold shrink-0" />
                      {ev.units.map((u) => (
                        <span key={u.id} className="font-mono text-[11px] tracking-wider px-2.5 py-1 rounded-full border border-gold/30 text-gold/90">
                          {u.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {ev.description && <p className="mt-4 text-sm text-zinc-500 leading-relaxed">{ev.description}</p>}
                  {ev.created_by && <p className="mt-3 font-mono text-[11px] tracking-wider text-zinc-600">CRIADO POR {ev.created_by.toUpperCase()}</p>}
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </AppShell>
  );
}
